import { useEffect, useRef, useState } from "react";
import { getUserTemplate } from "../services/sellerNetService";

// Hook to load and store template data only (static, unchanging)
export const useSellerTemplateData = (mainId, userId, companyId) => {
  const [templateData, setTemplateData] = useState(null);
  const hasLoadedRef = useRef("");
  const latestRequestRef = useRef(0);

  useEffect(() => {
    if (!mainId || !userId || !companyId) {
      hasLoadedRef.current = "";
      setTemplateData(null);
      return;
    }

    const contextKey = `${mainId}-${userId}-${companyId}`;
    if (hasLoadedRef.current === contextKey) return;
    hasLoadedRef.current = contextKey;
    setTemplateData(null);
    const requestId = ++latestRequestRef.current;

    const safeParse = (data) => {
      if (!data) return null;
      if (Array.isArray(data) || typeof data === "object") return data;
      try {
        return JSON.parse(data);
      } catch {
        return null;
      }
    };

    const toCamel = (s = "") =>
      s
        .replace(/[^a-zA-Z0-9]+([a-zA-Z0-9])/g, (_, c) => c.toUpperCase())
        .replace(/^([A-Z])/, (m) => m.toLowerCase());

    const parseValue = (item) => {
      if (!item) return "0.00";
      const v = item.value ?? item.rate ?? item.Cash ?? item.total ?? null;
      if (v == null) return "0.00";
      if (typeof v === "string") {
        const s = v.replace(/,/g, "").trim();
        return s === "" ? "0.00" : s;
      }
      return String(v);
    };

    const parseArraySection = (arr, options = {}) => {
      const { ignoreKeys = [], skipKeys = [] } = options;
      const parsed = [];
      const data = safeParse(arr);

      if (!Array.isArray(data)) return parsed;

      const ignoreSet = new Set(ignoreKeys);
      const skipSet = new Set(skipKeys);

      for (const it of data) {
        if (!it?.key) continue;
        if (ignoreSet.has(it.key.toString())) continue;
        if (skipSet.has(it.key.toString())) continue;

        // Handle payor logic
        let value = parseValue(it);
        try {
          const payorKey = Object.keys(it).find((k) =>
            k.toLowerCase().includes("payer"),
          );
          if (payorKey) {
            const payorVal = (it[payorKey] || "")
              .toString()
              .trim()
              .toLowerCase();
            if (payorVal && payorVal !== "seller") {
              value = "0.00";
            }
          }
        } catch {
          // ignore
        }

        parsed.push({
          ...it,
          camelKey: toCamel(it.key),
          value: value,
          total: parseValue(it),
        });
      }
      return parsed;
    };

    const fetchTemplate = async () => {
      try {
    //    console.log("fetchTemplate seller template data :", companyId);
        const response = await getUserTemplate(companyId);
        if (requestId !== latestRequestRef.current) return;
        const data = response;
        if (!data) return;

        // Parse section1 with ignore keys
        const section1 = parseArraySection(
          data?.seller_closing_costs_section1,
          {
            ignoreKeys: [
              "reconveyance_fee",
              "payoff_demand_statement",
              "prepayment_penality",
            ],
          },
        );

        // Parse section2
        const section2 = parseArraySection(data?.seller_closing_costs_section2);

        // Parse conventional costs
        const conventional = parseArraySection(
          data?.conventional_seller_closing_costs_section,
          {
            ignoreKeys: [
              "points_pct",
              "origination_factor_pct",
              "origination_factor",
            ],
          },
        );

        // Parse other costs (excluding corrective_work and buyers_fees)
        const otherCosts = parseArraySection(
          data?.seller_othercosts?.other_costs,
          {
            skipKeys: ["corrective_work", "buyers_fees"],
          },
        );

        // Parse payments to buyer
        const paymentsToBuyer = parseArraySection(
          data?.seller_othercosts?.payments_to_buyer,
          {
            skipKeys: ["days_interest"],
          },
        );

        // Parse loan-type specific sections
        const fha = parseArraySection(
          data?.fha_closing_costs ?? data?.fha_closing_costs_section,
          {
            ignoreKeys: [
              "points_pct",
              "origination_factor_pct",
              "origination_factor",
            ],
          },
        );

        const usda = parseArraySection(
          data?.usda_closing_costs ?? data?.usda_closing_costs_section,
          {
            ignoreKeys: [
              "points_pct",
              "origination_factor_pct",
              "origination_factor",
            ],
          },
        );

        const va = parseArraySection(
          data?.va_closing_costs ?? data?.va_closing_costs_section,
          {
            ignoreKeys: [
              "points_pct",
              "origination_factor_pct",
              "origination_factor",
            ],
          },
        );

        // Add corrective_work and buyers_fees to sellerOtherCosts
        const sellerOtherCosts = [...conventional];
        const extraOtherCosts = safeParse(data?.seller_othercosts?.other_costs);
        if (Array.isArray(extraOtherCosts)) {
          const EXTRA_KEYS = new Set(["corrective_work", "buyers_fees"]);

          extraOtherCosts.forEach((item) => {
            if (!item?.key) return;
            const key = item.key.toString();

            if (EXTRA_KEYS.has(key)) {
              // Check for duplicates
              const isDuplicate = sellerOtherCosts.some(
                (existing) => existing.key === key,
              );
              if (isDuplicate) return;

              const defaults = {
                unit: "$",
                label:
                  key === "corrective_work"
                    ? "Corrective Work"
                    : key === "buyers_fees"
                      ? "Buyer's Fees"
                      : key,
                sr_no:
                  key === "corrective_work"
                    ? 7
                    : key === "buyers_fees"
                      ? 8
                      : undefined,
              };

              sellerOtherCosts.push({
                ...defaults,
                ...item,
                camelKey: toCamel(key),
                value: parseValue(item),
                total: parseValue(item),
              });
            }
          });
        }

        // Create templateSellerOtherCosts with loan-type variants
        const templateSellerOtherCosts = {
          conventional: sellerOtherCosts,
          fha: fha,
          usda: usda,
          va: va,
        };

        // Extract transfer tax from section1
        const transferTax =
          section1.find((item) => item.key === "transfer_tax_rate_per")
            ?.value || "0.00";

        setTemplateData({
          contextKey,
          sellerSections: {
            section1,
            section2,
            sellerOtherCosts,
            otherCosts,
            paymentsToBuyer,
            templateSellerOtherCosts,
          },
          transferTax,
        });
      } catch (err) {
        if (requestId !== latestRequestRef.current) return;
        console.error("Error loading seller template:", err);
      }
    };

    fetchTemplate();
  }, [mainId, userId, companyId]);

  return templateData;
};
